import { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import JsBarcode from 'jsbarcode';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Bluetooth, Printer, Loader2, RefreshCw } from 'lucide-react';
import {
  bluetoothAvailable, printLabelsBluetooth, rememberedPrinterName,
  requestPrinter, forgetPrinter,
} from '@/lib/printer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable label print dialog. Accepts one or more items and offers both
 * Bluetooth printing (Xprinter XP-P322B) and the browser print dialog.
 */
export default function LabelPrintDialog({ open, onOpenChange, items = [] }) {
  const [btBusy, setBtBusy] = useState(false);
  const [btName, setBtName] = useState(rememberedPrinterName());
  const [effectiveItems, setEffectiveItems] = useState(items);
  const printAreaRef = useRef(null);

  const btSupported = useMemo(() => bluetoothAvailable(), []);

  // Whenever the dialog opens with items, backfill any missing barcodes on
  // the server so every label has a proper EAN-13 that a scanner can read.
  useEffect(() => {
    if (!open || items.length === 0) {
      setEffectiveItems(items);
      return;
    }
    let cancelled = false;
    (async () => {
      const filled = await Promise.all(items.map(async (it) => {
        if (it.barcode) return it;
        if (!it.id) return it;  // can't remediate without a product id
        try {
          const res = await axios.post(`${API}/products/${it.id}/ensure-barcode`);
          return res.data;
        } catch (err) {
          console.error('ensure-barcode failed for', it.sku, err);
          return it;
        }
      }));
      if (!cancelled) setEffectiveItems(filled);
    })();
    return () => { cancelled = true; };
  }, [open, items]);

  useEffect(() => {
    if (!open || effectiveItems.length === 0) return;
    // Render barcodes into the SVG placeholders once the dialog is on screen.
    requestAnimationFrame(() => {
      effectiveItems.forEach((it) => {
        const svg = document.getElementById(`label-svg-${it.id || it.sku}`);
        if (!svg) return;
        // Fallback: if the item still has no EAN-13 (rare — e.g. no id), use
        // the SKU as a CODE128 so the label is never barcode-less.
        const value = it.barcode || it.sku;
        if (!value) return;
        const format = value.length === 13 && /^\d+$/.test(value) ? 'EAN13' : 'CODE128';
        try {
          JsBarcode(svg, value, {
            format, width: 2, height: 60, displayValue: true, fontSize: 14, margin: 2,
          });
        } catch (err) {
          console.error('barcode render failed', err);
        }
      });
    });
  }, [open, effectiveItems]);

  const handleBluetoothPrint = async () => {
    setBtBusy(true);
    try {
      await printLabelsBluetooth(effectiveItems);
      setBtName(rememberedPrinterName());
      toast.success(`Printed ${effectiveItems.length} label${effectiveItems.length === 1 ? '' : 's'} via Bluetooth`);
    } catch (err) {
      console.error('bluetooth print failed', err);
      if (err?.name === 'NotFoundError') {
        toast.info('No printer selected');
      } else {
        toast.error(`Bluetooth print failed: ${err.message || err}. Try the browser Print button.`);
      }
    } finally {
      setBtBusy(false);
    }
  };

  const handlePairPrinter = async () => {
    try {
      const dev = await requestPrinter();
      setBtName(dev.name || 'Paired');
      toast.success(`Paired with ${dev.name || 'printer'}`);
    } catch (err) {
      if (err?.name !== 'NotFoundError') {
        toast.error(err.message || 'Pairing failed');
      }
    }
  };

  const handleForget = () => {
    forgetPrinter();
    setBtName(null);
    toast.info('Printer unpaired — next print will prompt you to pick one');
  };

  const handleBrowserPrint = () => {
    // Add a body class so our print stylesheet only shows the label sheet.
    document.body.classList.add('printing-labels');
    // Delay to let the class settle in.
    setTimeout(() => {
      window.print();
      document.body.classList.remove('printing-labels');
    }, 50);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5 text-[#1680e5]" />
            Print label{items.length === 1 ? '' : `s (${items.length})`}
          </DialogTitle>
          <DialogDescription>
            Optimised for the Xprinter XP-P322B (80&nbsp;mm thermal, EAN-13 native).
          </DialogDescription>
        </DialogHeader>

        {/* Bluetooth status bar */}
        {btSupported && (
          <div className="flex items-center justify-between rounded-md border bg-slate-50 px-3 py-2 text-sm">
            <div className="flex items-center gap-2">
              <Bluetooth className={`h-4 w-4 ${btName ? 'text-[#1680e5]' : 'text-slate-400'}`} />
              <span className="text-slate-700">
                {btName ? (
                  <>Printer: <span className="font-medium">{btName}</span></>
                ) : (
                  'No printer paired yet'
                )}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost" size="sm" onClick={handlePairPrinter}
                data-testid="pair-printer-btn"
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                {btName ? 'Change' : 'Pair'}
              </Button>
              {btName && (
                <Button variant="ghost" size="sm" onClick={handleForget} data-testid="forget-printer-btn">
                  Forget
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Label preview */}
        <div
          ref={printAreaRef}
          className="label-print-sheet max-h-[400px] overflow-y-auto space-y-3 border rounded-md p-4 bg-white"
          data-testid="label-preview"
        >
          {effectiveItems.length === 0 ? (
            <div className="text-center text-slate-500 py-8">No items selected</div>
          ) : (
            effectiveItems.map((it) => (
              <LabelPreview key={it.id || it.sku} item={it} />
            ))
          )}
        </div>

        <DialogFooter className="gap-2">
          {btSupported && (
            <Button
              onClick={handleBluetoothPrint}
              disabled={btBusy || items.length === 0}
              className="bg-[#1680e5] hover:bg-[#1680e5]/90"
              data-testid="bluetooth-print-btn"
            >
              {btBusy ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Printing…</>
              ) : (
                <><Bluetooth className="h-4 w-4 mr-2" />Print via Bluetooth</>
              )}
            </Button>
          )}
          <Button
            variant={btSupported ? 'outline' : 'default'}
            onClick={handleBrowserPrint}
            disabled={items.length === 0}
            className={btSupported ? '' : 'bg-[#1680e5] hover:bg-[#1680e5]/90'}
            data-testid="browser-print-btn"
          >
            <Printer className="h-4 w-4 mr-2" />
            Print via browser dialog
          </Button>
        </DialogFooter>
        {!btSupported && (
          <p className="text-xs text-slate-500">
            Web Bluetooth isn&apos;t supported by this browser. Use the browser print dialog and
            select your Xprinter driver, or open Zippy WMS in Chrome / Android Chrome for one-tap Bluetooth printing.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function LabelPreview({ item }) {
  const truncated = (item.name || '').length > 32
    ? `${item.name.slice(0, 31)}…`
    : item.name;
  return (
    <div className="wms-label" data-testid={`label-${item.sku}`}>
      <div className="wms-label-name">{truncated}</div>
      <svg id={`label-svg-${item.id || item.sku}`} className="wms-label-barcode" />
      <div className="wms-label-sku">{item.sku}</div>
    </div>
  );
}
